/**
 * Forgot PIN recovery hook.
 *
 * Orchestrates: mnemonic validation, wallet ownership verification via
 * BIP-85 child derivation + Evolu owner comparison, PIN re-encryption,
 * and wallet unlock.
 *
 * Tier: All tiers (core auth infrastructure).
 *
 * @module useForgotPin
 */

import { useCallback, useRef, useState } from 'react';
import { useEvolu } from '@evolu/react';
import { Mnemonic } from '@evolu/common';
import { mnemonicToOwnerSecret, createAppOwner } from '@evolu/common/local-first';
import { validateMnemonic, deriveChildMnemonic } from '@/lib/crypto';
import { encryptMnemonic } from '@/lib/crypto-encrypt';
import { createPlatformServices } from '@/lib/platform/index';
import { useMnemonic } from '@/contexts/MnemonicProvider';
import { useAuth } from '@/contexts/AuthProvider';

/** SecureStorage key for encrypted mnemonic blob. */
const ENCRYPTED_MNEMONIC_KEY = 'encrypted_mnemonic';

/** BIP-85 index for Evolu child mnemonic (per ADR-014). */
const EVOLU_CHILD_INDEX = 0;

/** Recovery flow steps. */
export type ForgotPinStep = 'mnemonic_entry' | 'pin_setup';

/** Return type for the useForgotPin hook. */
export interface UseForgotPinReturn {
  /** Current step in the recovery flow. */
  readonly step: ForgotPinStep;
  /** Error message, or undefined if no error. */
  readonly error: string | undefined;
  /** Whether an async operation is in progress. */
  readonly loading: boolean;
  /** Submit the mnemonic for validation and wallet matching. */
  submitMnemonic(mnemonic: string): Promise<void>;
  /** Submit the new PIN to re-encrypt and unlock. */
  submitNewPin(pin: string): Promise<void>;
}

/**
 * Verify that a master mnemonic matches the current wallet.
 *
 * Derives the BIP-85 child mnemonic at index 0 (Evolu child),
 * creates an Evolu owner from it, and compares the owner ID
 * against the current Evolu instance's owner.
 *
 * @param enteredMnemonic - The master mnemonic entered by the user
 * @param evolu - The current Evolu instance
 * @returns true if the mnemonic matches the current wallet
 */
async function verifyMnemonicMatchesWallet(
  enteredMnemonic: string,
  evolu: ReturnType<typeof useEvolu>,
): Promise<boolean> {
  try {
    const childMnemonic = deriveChildMnemonic(enteredMnemonic, EVOLU_CHILD_INDEX);
    const mnemonicResult = Mnemonic.from(childMnemonic);
    if (!mnemonicResult.ok) return false;

    const ownerSecret = mnemonicToOwnerSecret(mnemonicResult.value);
    const derivedOwner = createAppOwner(ownerSecret);

    const currentOwner = await (evolu as { appOwner: Promise<{ id: string }> }).appOwner;
    return derivedOwner.id === currentOwner.id;
  } catch {
    return false;
  }
}

/**
 * Hook for the forgot PIN recovery flow.
 *
 * State machine: mnemonic_entry -> pin_setup -> unlock.
 * Verifies mnemonic ownership before allowing PIN reset.
 *
 * Tier: All tiers (core auth infrastructure).
 *
 * @returns UseForgotPinReturn with step state and actions
 */
export function useForgotPin(): UseForgotPinReturn {
  const [step, setStep] = useState<ForgotPinStep>('mnemonic_entry');
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const verifiedMnemonicRef = useRef<string | null>(null);
  const evolu = useEvolu();
  const { unlock: mnemonicUnlock } = useMnemonic();
  const { unlock: authUnlock } = useAuth();

  const submitMnemonic = useCallback(
    async (mnemonic: string): Promise<void> => {
      setLoading(true);
      setError(undefined);

      if (!validateMnemonic(mnemonic)) {
        setError('Invalid recovery phrase. Please check your words and try again.');
        setLoading(false);
        return;
      }

      const matches = await verifyMnemonicMatchesWallet(mnemonic, evolu);

      if (!matches) {
        setError("This recovery phrase doesn't match your wallet.");
        setLoading(false);
        return;
      }

      verifiedMnemonicRef.current = mnemonic;
      setStep('pin_setup');
      setLoading(false);
    },
    [evolu],
  );

  const submitNewPin = useCallback(
    async (pin: string): Promise<void> => {
      const verifiedMnemonic = verifiedMnemonicRef.current;
      if (!verifiedMnemonic) {
        setError('No verified mnemonic. Please start again.');
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        const encrypted = await encryptMnemonic(verifiedMnemonic, pin);
        const services = await createPlatformServices();
        await services.secureStorage.store(ENCRYPTED_MNEMONIC_KEY, encrypted);

        mnemonicUnlock(verifiedMnemonic);
        authUnlock();

        verifiedMnemonicRef.current = null;
      } catch {
        setError('Failed to reset PIN. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [mnemonicUnlock, authUnlock],
  );

  return {
    step,
    error,
    loading,
    submitMnemonic,
    submitNewPin,
  };
}
